import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { formatNumber } from '@lichtblick/localization';
import { Headline } from '@lichtblick/photon/atoms/headline/headline';
import { Text } from '@lichtblick/photon/atoms/text/text';
import { TextCTA } from '@lichtblick/photon/atoms/text-cta/text-cta';
import { InfoXs } from '@lichtblick/photon/icons/symbols/info-xs';
import { Stepper } from '@lichtblick/photon/molecules/stepper';
import { Modal } from '@lichtblick/photon/organisms/modal/modal';
import { useTracker } from '@lichtblick/tracker';
import classNames from 'classnames';
import { useCallback, useState } from 'react';
import styles from './calculator-usage-input.module.scss';
import { carEfficiencyLimit, homeChargingPartLimit, mileageLimit } from './constants';
const calculateUsage = (mileage)=>Math.round(carEfficiencyLimit.default * (homeChargingPartLimit.default / 100) * mileage / 100) * 100;
export const ChargingUsageInput = ({ onChange, usageRef })=>{
    const { trackCustomEvent } = useTracker();
    const [mileage, setMileage] = useState(mileageLimit.default);
    const [isInfoModalOpen, setIsInfoModalOpen] = useState(false);
    // biome-ignore lint/correctness/useExhaustiveDependencies: TODO
    const handleStepperChange = useCallback((mileage)=>{
        trackCustomEvent({
            eventName: 'tariff_calculator_interaction'
        }, undefined, true);
        if (mileage) {
            setMileage(mileage);
            if (usageRef.current) {
                usageRef.current.value = calculateUsage(mileage).toString();
                onChange(usageRef.current.value, mileage);
            }
        }
    }, [
        setMileage
    ]);
    return /*#__PURE__*/ _jsxs("div", {
        children: [
            /*#__PURE__*/ _jsx(Stepper, {
                defaultValue: mileage,
                label: "Fahrleistung in km/Jahr",
                min: mileageLimit.min,
                name: "mileage",
                onChange: handleStepperChange,
                step: mileageLimit.step
            }),
            /*#__PURE__*/ _jsxs("div", {
                className: styles['calculator-input-row'],
                children: [
                    /*#__PURE__*/ _jsxs(Text, {
                        size: "xs",
                        children: [
                            "Dein Verbrauch",
                            ' ',
                            /*#__PURE__*/ _jsx("input", {
                                "aria-invalid": usageRef.current?.checkValidity() === false,
                                "aria-label": "Verbrauch in kWh/Jahr",
                                className: classNames(styles['calculator-usage-input'], usageRef.current?.checkValidity() === false && styles['calculator-usage-input-error']),
                                defaultValue: calculateUsage(mileageLimit.default),
                                name: "carUsage",
                                onChange: (event)=>onChange(event.target.value, mileage),
                                onClick: ()=>trackCustomEvent({
                                        eventName: 'tariff_calculator_interaction'
                                    }, undefined, true),
                                onInvalid: (e)=>e.preventDefault(),
                                ref: usageRef,
                                required: true,
                                type: "number"
                            }),
                            ' ',
                            "kWh/Jahr"
                        ]
                    }),
                    /*#__PURE__*/ _jsx(TextCTA, {
                        ariaLabel: "Informationen zur Berechnung des Autoverbrauchs zeigen",
                        as: "button",
                        icon: /*#__PURE__*/ _jsx(InfoXs, {}),
                        onClick: ()=>setIsInfoModalOpen(true),
                        type: "button"
                    })
                ]
            }),
            /*#__PURE__*/ _jsx(Modal, {
                footerContent: /*#__PURE__*/ _jsx(TextCTA, {
                    as: "button",
                    "data-testid": "button-modal-close",
                    onClick: ()=>setIsInfoModalOpen(false),
                    children: "Ok, verstanden"
                }),
                isOpen: isInfoModalOpen,
                onPointerDownOutside: ()=>setIsInfoModalOpen(false),
                title: "So berechnet LichtBlick den Verbrauchsanteil für euer E-Auto",
                children: /*#__PURE__*/ _jsxs("div", {
                    className: styles['modal-content'],
                    children: [
                        /*#__PURE__*/ _jsx(Headline, {
                            renderAs: 'h2',
                            size: "s",
                            children: "So berechnet LichtBlick den Verbrauchsanteil für euer E-Auto"
                        }),
                        /*#__PURE__*/ _jsxs(Text, {
                            renderAs: 'p',
                            children: [
                                "Solltet ihr euren Jahresverbruach für das Laden des E-Autos zu Hause kennen, könnt ihr es direkt eintragen. Besitzt ihr noch keine längeren Erfahrungswerte, schätzen wir den Verbrauch für euch: Bis zu",
                                ' ',
                                homeChargingPartLimit.default,
                                " % der E-Auto-Ladevorgänge finden im privaten Umfeld statt, beispielsweise am eigenen Stellplatz. Für den durchschnittlichen Stromverbrauch gehen wir von",
                                ' ',
                                formatNumber(carEfficiencyLimit.default),
                                " kWh/km aus. Der tatsächliche Verbrauch hängt – neben dem Automodell – auch von der Fahrweise und den Wetterbedingungen ab. Daraus ergibt sich die Rechnung:"
                            ]
                        }),
                        /*#__PURE__*/ _jsxs(Text, {
                            renderAs: 'p',
                            children: [
                                "Verbrauch = Fahrleistung pro Jahr x ",
                                homeChargingPartLimit.default,
                                " % zu Hause Laden x",
                                ' ',
                                formatNumber(carEfficiencyLimit.default),
                                " kWh/km."
                            ]
                        }),
                        /*#__PURE__*/ _jsx(Text, {
                            renderAs: 'p',
                            children: "Dieser Wert wird verwendet, wenn ihr die Jahreskilometer für das E-Auto angebt."
                        })
                    ]
                })
            })
        ]
    });
};

//# sourceMappingURL=ChargingUsageInput.js.map